import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Spinner,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  AlertRegular,
  ArrowClockwiseRegular,
  Delete20Regular,
  Dismiss20Regular,
  Edit20Regular,
  EyeOffRegular,
  EyeRegular,
  Search20Regular,
} from '@fluentui/react-icons';
import { formatDateToNow, toEpochMs } from '@moko/utils';
import type {
  IWorkComment,
  IUpdateWorkCommentRequest,
  IUpdateWorkCommentResponse,
  ICreateWorkCommentRequest,
  ICreateWorkCommentResponse,
  IPagedCollection,
  NotifyAudience,
  CommentVisibility,
} from '@moko/utils';
import RenderIf from '../RenderIf';
import useFetch from '../hooks/useFetch';
import type { WorkComponentProps, IWorkCommentsServices } from './types';
import { CommentForm } from './CommentForm';
import type { CommentFormValues } from './VisibilityRolesFields';
import {
  DEFAULT_VISIBILITY_FORM,
  isPrivateVisibility,
  parseVisibility,
  publicRoleLabels,
  serializeVisibility,
} from './commentVisibility';
import { DataGridFilters } from '../DataGrid/Filter/DataGridFilters';
import {
  FilterType,
  serializeFilters,
  type IFilterableColumn,
  type IActiveFilter,
} from '../DataGrid/Filter';

const NOTIFY_MANAGERS: NotifyAudience = 'Managers';

const DEFAULT_VISIBILITY: CommentVisibility = 'private';

const EDIT_WINDOW_MS = 5 * 60 * 1000;

const isWithinEditWindow = (
  comment: Pick<IWorkComment, 'createdAt' | 'modifiedAt'>,
  now: number
): boolean => {
  const reference =
    (comment.modifiedAt ? toEpochMs(comment.modifiedAt) : NaN) ||
    (comment.createdAt ? toEpochMs(comment.createdAt) : NaN);
  if (Number.isNaN(reference)) return false;
  return now - reference <= EDIT_WINDOW_MS;
};

interface WorkCommentsProps extends WorkComponentProps {
  workId: number | undefined;
  services: IWorkCommentsServices;
  readOnly?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    // Relative height so the 3 sections fit within the available viewport
    // on both desktop and mobile MFE hosts.
    height: '100%',
    maxHeight: '65vh',
    minHeight: '280px',
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    ...shorthands.overflow('hidden'),
  },
  // Section 1 — header
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    flexShrink: 0,
    width: '100%',
    boxSizing: 'border-box',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  headerActions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    flex: '1 1 auto',
    justifyContent: 'flex-end',
    minWidth: 0,
  },
  searchInput: {
    flex: '1 1 160px',
    minWidth: 0,
    maxWidth: '320px',
  },
  // Section 2 — thread (scrollable)
  thread: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalL),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  empty: {
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    ...shorthands.margin('auto', '0'),
  },
  // A single comment in the thread — no box, chat-like.
  item: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
  itemBody: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
    minWidth: 0,
    flexGrow: 1,
  },
  // Name + date, always on a single line. Name truncates; date never wraps.
  identityRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
  author: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: 0,
    flexGrow: 1,
    flexShrink: 1,
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  // Vis + bell badges, rendered below the comment content.
  badges: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  badgeIcon: {
    fontSize: '18px',
  },
  iconPublic: { color: tokens.colorPaletteGreenForeground1 },
  iconPrivate: { color: tokens.colorPaletteDarkOrangeForeground1 },
  iconNotify: { color: tokens.colorBrandForeground1 },
  // Content row: text (multi-line) + action icons pinned top-right.
  contentRow: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  itemActions: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalXXS),
    flexShrink: 0,
  },
  body: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: tokens.colorNeutralForeground1,
    flexGrow: 1,
    minWidth: 0,
  },
  edited: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginLeft: tokens.spacingHorizontalXS,
  },
  // Section 3 — new comment composer
  composer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    flexShrink: 0,
  },
  // Subtle delete icon button — red danger foreground.
  deleteIcon: {
    color: tokens.colorPaletteRedForeground1,
    ':hover': { color: tokens.colorPaletteRedForeground1 },
    ':hover:active': { color: tokens.colorPaletteRedForeground1 },
  },
  // Destructive confirm action — red danger tokens.
  dangerButton: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    ...shorthands.borderColor(tokens.colorPaletteRedBackground3),
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorPaletteRedForeground1,
      ...shorthands.borderColor(tokens.colorPaletteRedForeground1),
      color: tokens.colorNeutralForegroundOnBrand,
    },
    ':hover:active': {
      backgroundColor: tokens.colorPaletteRedForeground3,
      ...shorthands.borderColor(tokens.colorPaletteRedForeground3),
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
});

interface IWorkCommentWithEditMode extends IWorkComment {
  InEditMode: boolean;
  edited: boolean;
}

const toEditModeComment = (c: IWorkComment): IWorkCommentWithEditMode => {
  const edited = c.modifiedAt !== c.createdAt;
  return {
    ...c,
    createdAtFormatted: formatDateToNow(c.createdAt),
    modifiedByUserName: edited ? c.modifiedByUserName : undefined,
    modifiedAtFormatted: edited ? formatDateToNow(c.modifiedAt) : undefined,
    InEditMode: false,
    visibility: c.visibility ?? DEFAULT_VISIBILITY,
    edited,
  };
};

const getInitials = (name?: string) =>
  (name ?? '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

const COMMENT_FILTER_COLUMNS: IFilterableColumn[] = [
  {
    key: 'content',
    label: 'Content',
    type: FilterType.String,
    kind: 'text',
  },
  {
    key: 'visibility',
    label: 'Visibility',
    type: FilterType.String,
    kind: 'select',
    options: [
      { value: 'public', label: 'Public' },
      { value: 'private', label: 'Private' },
    ],
  },
];

const WorkComments = ({
  workId,
  services,
  setIsInProgress,
  setSuccessMessage,
  setErrorMessage,
  readOnly = false,
}: WorkCommentsProps) => {
  const [comments, setComments] = useState<IWorkCommentWithEditMode[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [filters, setFilters] = useState<IActiveFilter[]>([]);
  // Tracks an in-flight submit: 'new' for the composer, or a comment id for
  // an edit. Used to show a spinner and disable/block all CTAs and Enter.
  const [submitting, setSubmitting] = useState<number | 'new' | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const isSubmitting = submitting !== null || deletingId !== null;
  const classes = useStyles();

  const nowMs = Date.now();

  const filtersStr = useMemo(() => {
    const str = serializeFilters(filters);
    return str || undefined;
  }, [filters]);

  const handleFilterApply = useCallback((filter: IActiveFilter) => {
    setFilters((prev) => [...prev, filter]);
  }, []);

  const handleFilterUpdate = useCallback(
    (index: number, filter: IActiveFilter) => {
      setFilters((prev) => prev.map((f, i) => (i === index ? filter : f)));
    },
    []
  );

  const handleFilterRemove = useCallback((index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const {
    data,
    loading,
    refetch: handleRefresh,
  } = useFetch<IPagedCollection<IWorkComment>>({
    queryKey: [workId, committedSearch, filtersStr],
    enabled: workId !== undefined,
    queryFn: (abortController) => {
      if (workId === undefined)
        return Promise.reject(new Error('workId not provided'));
      return services.getWorkComments(
        abortController,
        workId,
        undefined,
        undefined,
        undefined,
        committedSearch || undefined,
        filtersStr
      );
    },
    onLoading: () => setIsInProgress(true),
    onLoaded: () => setIsInProgress(false),
    onError: (error) => {
      setIsInProgress(false);
      setErrorMessage(error.message || 'Failed to load comments');
    },
  });

  useEffect(() => {
    if (data) setComments(data.items.map(toEditModeComment));
  }, [data]);

  const updateComment = (
    id: number,
    changes: Partial<IWorkCommentWithEditMode>
  ) =>
    setComments((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...changes } : c))
    );

  const handleEditCommentButton = (id: number) =>
    updateComment(id, { InEditMode: true });

  const handleCancelButtonClick = (id: number) =>
    updateComment(id, { InEditMode: false });

  const handleConfirmDelete = async () => {
    if (confirmDeleteId == null) return;
    const id = confirmDeleteId;

    const abortController = new AbortController();
    setDeletingId(id);
    setIsInProgress(true);
    try {
      await services.deleteWorkComment(abortController, id);
      setComments((prev) => prev.filter((c) => c.id !== id));
      setSuccessMessage(`Comment with id ${id} deleted`);
      setConfirmDeleteId(null);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to delete comment'
      );
    } finally {
      setDeletingId(null);
      setIsInProgress(false);
    }
  };

  const handleSaveComment = async (
    id: number,
    values: CommentFormValues
  ): Promise<boolean> => {
    const abortController = new AbortController();
    const notifyAudience = values.notifyManagers ? NOTIFY_MANAGERS : undefined;
    const visibility = serializeVisibility(values);

    setSubmitting(id);
    setIsInProgress(true);
    const req: IUpdateWorkCommentRequest = {
      commentId: id,
      content: values.content,
      notifyAudience,
      visibility,
    };
    try {
      const res: IUpdateWorkCommentResponse = await services.updateWorkComment(
        abortController,
        req
      );
      updateComment(id, {
        content: values.content,
        modifiedAt: res.modifiedAt,
        modifiedAtFormatted: formatDateToNow(res.modifiedAt),
        modifiedByUserName: res.modifiedByUserName,
        notifyAudience,
        visibility,
        InEditMode: false,
        edited: true,
      });
      setSuccessMessage(`Comment with id ${id} successfully saved`);
      return true;
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to save comment'
      );
      return false;
    } finally {
      setSubmitting(null);
      setIsInProgress(false);
    }
  };

  const handleCreateComment = async (
    values: CommentFormValues
  ): Promise<boolean> => {
    const abortController = new AbortController();
    if (workId === undefined) {
      setErrorMessage('workId not provided');
      return false;
    }

    const notifyAudience = values.notifyManagers ? NOTIFY_MANAGERS : undefined;
    const visibility = serializeVisibility(values);

    setSubmitting('new');
    setIsInProgress(true);
    const req: ICreateWorkCommentRequest = {
      workId,
      content: values.content,
      notifyAudience,
      visibility,
    };
    try {
      const res: ICreateWorkCommentResponse = await services.createWorkComment(
        abortController,
        req
      );
      setComments((prev) => [
        {
          id: res.id,
          content: values.content,
          createdAt: res.createdAt,
          createdAtFormatted: formatDateToNow(res.createdAt),
          createByUserName: res.createByUserName,
          InEditMode: false,
          notifyAudience,
          visibility,
          edited: false,
        },
        ...prev,
      ]);
      setSuccessMessage(`Comment with id ${res.id} successfully saved`);
      return true;
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to create comment'
      );
      return false;
    } finally {
      setSubmitting(null);
      setIsInProgress(false);
    }
  };

  const renderVisibilityAndNotify = (
    visibility: string | undefined,
    notifyAudience: NotifyAudience | undefined
  ) => (
    <>
      {isPrivateVisibility(visibility) ? (
        <span className={classes.badge}>
          <EyeOffRegular
            className={mergeClasses(classes.badgeIcon, classes.iconPrivate)}
          />
        </span>
      ) : (
        <span className={classes.badge}>
          <EyeRegular
            className={mergeClasses(classes.badgeIcon, classes.iconPublic)}
          />
          {publicRoleLabels(visibility)}
        </span>
      )}
      <span className={classes.badge}>
        <AlertRegular
          className={mergeClasses(classes.badgeIcon, classes.iconNotify)}
        />
        {notifyAudience ?? 'None'}
      </span>
    </>
  );

  const commentFormValues = (
    comment: IWorkCommentWithEditMode
  ): CommentFormValues => {
    const { isPublic, roles } = parseVisibility(comment.visibility);
    return {
      content: comment.content,
      isPublic,
      roles,
      notifyManagers: comment.notifyAudience === NOTIFY_MANAGERS,
    };
  };

  return (
    <div className={classes.root}>
      {/* Section 1 — header */}
      <div className={classes.header}>
        <Text
          weight="semibold"
          style={{ whiteSpace: 'nowrap' }}
        >{`Comments (${comments.length})`}</Text>
        <div className={classes.headerActions}>
          <Input
            className={classes.searchInput}
            size="small"
            placeholder="Search comments…"
            value={searchValue}
            onChange={(_, d) => setSearchValue(d.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setCommittedSearch(searchValue);
            }}
            contentBefore={<Search20Regular />}
            contentAfter={
              searchValue ? (
                <Dismiss20Regular
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSearchValue('');
                    setCommittedSearch('');
                  }}
                />
              ) : undefined
            }
          />
          <DataGridFilters
            columns={COMMENT_FILTER_COLUMNS}
            filters={filters}
            onApply={handleFilterApply}
            onUpdate={handleFilterUpdate}
            onRemove={handleFilterRemove}
          />
          <Button
            aria-label="Refresh comments"
            appearance="subtle"
            icon={<ArrowClockwiseRegular />}
            disabled={loading}
            onClick={handleRefresh}
          />
        </div>
      </div>

      {/* Section 2 — thread */}
      <div className={classes.thread}>
        <RenderIf condition={loading}>
          <div className={classes.empty}>
            <Spinner size="small" label="Loading comments…" />
          </div>
        </RenderIf>

        <RenderIf condition={!loading && comments.length === 0}>
          <div className={classes.empty}>No comments yet.</div>
        </RenderIf>

        {!loading &&
          comments.map((comment) => (
            <div key={comment.id} className={classes.item}>
              <Avatar
                color="colorful"
                name={comment.createByUserName}
                initials={getInitials(comment.createByUserName)}
              />
              <div className={classes.itemBody}>
                <div className={classes.identityRow}>
                  <Text className={classes.author} truncate>
                    {comment.createByUserName ?? 'Unknown'}
                  </Text>
                  <Text className={classes.meta}>
                    {`#${comment.id} · ${comment.createdAtFormatted ?? ''}`}
                  </Text>
                </div>

                <RenderIf condition={!comment.InEditMode}>
                  <div className={classes.contentRow}>
                    <Text className={classes.body}>
                      {comment.content}
                      <RenderIf condition={comment.edited}>
                        <span className={classes.edited}>
                          {comment.modifiedAtFormatted
                            ? `(edited ${comment.modifiedAtFormatted})`
                            : '(edited)'}
                        </span>
                      </RenderIf>
                    </Text>
                    <RenderIf
                      condition={
                        !readOnly && isWithinEditWindow(comment, nowMs)
                      }
                    >
                      <div className={classes.itemActions}>
                        <Button
                          aria-label="Edit"
                          appearance="subtle"
                          size="small"
                          icon={<Edit20Regular />}
                          disabled={isSubmitting}
                          onClick={() => handleEditCommentButton(comment.id)}
                        />
                        <Button
                          aria-label="Delete"
                          appearance="subtle"
                          size="small"
                          className={classes.deleteIcon}
                          icon={
                            deletingId === comment.id ? (
                              <Spinner size="tiny" />
                            ) : (
                              <Delete20Regular />
                            )
                          }
                          disabled={isSubmitting}
                          onClick={() => setConfirmDeleteId(comment.id)}
                        />
                      </div>
                    </RenderIf>
                  </div>
                  <div className={classes.badges}>
                    {renderVisibilityAndNotify(
                      comment.visibility ?? DEFAULT_VISIBILITY,
                      comment.notifyAudience
                    )}
                  </div>
                </RenderIf>

                <RenderIf condition={comment.InEditMode}>
                  <CommentForm
                    mode="edit"
                    defaultValues={commentFormValues(comment)}
                    submitting={submitting === comment.id}
                    disabled={isSubmitting}
                    onSubmit={(values) => handleSaveComment(comment.id, values)}
                    onCancel={() => handleCancelButtonClick(comment.id)}
                  />
                </RenderIf>
              </div>
            </div>
          ))}
      </div>

      <RenderIf condition={!readOnly}>
        <div className={classes.composer}>
          <CommentForm
            mode="new"
            defaultValues={{
              content: '',
              ...DEFAULT_VISIBILITY_FORM,
              notifyManagers: false,
            }}
            submitting={submitting === 'new'}
            disabled={isSubmitting}
            onSubmit={handleCreateComment}
          />
        </div>
      </RenderIf>

      <Dialog
        open={confirmDeleteId != null}
        onOpenChange={(_, data) => {
          if (!data.open && deletingId == null) setConfirmDeleteId(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete comment?</DialogTitle>
            <DialogContent>
              This comment will be permanently deleted. Are you sure?
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setConfirmDeleteId(null)}
                disabled={deletingId != null}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                className={classes.dangerButton}
                onClick={handleConfirmDelete}
                disabled={deletingId != null}
                icon={deletingId != null ? <Spinner size="tiny" /> : undefined}
              >
                {deletingId != null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default WorkComments;
